import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { kebabCase } from 'lodash';
import { getCorrection, listCorrections, updateCorrection, deleteCorrection } from '../Api/Correction/CorrectionApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { User } from './User';
import { fetchAttributions } from './Attribution';

export interface Correction {
  id: string;
  combinedAssetId: string;
  combinedAssetNumber: string;
  customerName: string | null;
  supplierName: string | null;
  paidInRunId: number;
  paidInRunDate: string;
  paidInRunStatus: string;
  correctsRunId: number;
  correctsRunDate: string;
  correctsRunStatus: string;
  revenueRecurringCorrection: number | null;
  revenueOneTimeCorrection: number | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: User | null;
  tsdName: string | null;
  salesAgentName: string | null;
  commissionsStatus: string | null;
  actualFirstCommissionRun: string | null;
  actualDecommissionedDate: string | null;
  paidRunActive: boolean | null;
  assetSfId: string | null;
  customerId: string | null;
  updatedBy: User | null;
  combinedAssetType: 'asset' | 'provisional_asset';
  paidInRunName: string;
  correctsRunName: string;
}

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const correctionSubject = new BehaviorSubject<Correction>({} as Correction);
const tallyCorrectionsSubject = new BehaviorSubject<PaginatedResponse<Correction>>({} as PaginatedResponse<Correction>);
const correctionLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyCorrectionsLoadingSubject = new BehaviorSubject<boolean>(false);
const currentFiltersSubject = new BehaviorSubject<ApiAdvancedFilter | undefined>(undefined);

const clearError = () => addError('correction', undefined);

export const getCorrectionSubject = (): BehaviorSubject<Correction> => correctionSubject;
export const getCorrectionLoadingSubject = (): BehaviorSubject<boolean> => correctionLoadingSubject;
export const getTallyCorrectionsSubject = (): BehaviorSubject<PaginatedResponse<Correction>> => tallyCorrectionsSubject;
export const getTallyCorrectionsLoadingSubject = (): BehaviorSubject<boolean> => tallyCorrectionsLoadingSubject;

export const fetchCorrections = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyCorrectionsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyCorrectionsLoadingSubject.next(true);
  currentFiltersSubject.next(filters);
  const { data, error } = await listCorrections(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    tallyCorrectionsSubject.next(data);
  }
  if (error) addError('correction', error.message);

  newFetchNumber === currentFetchNumber && tallyCorrectionsLoadingSubject.next(false);
};

export const fetchCorrectionById = async (id: string): Promise<void | number> => {
  correctionLoadingSubject.next(true);

  const { data, error } = await getCorrection(id);

  if (data) {
    clearError();
    correctionSubject.next(data);
  }
  if (error) {
    addError('correction', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  correctionLoadingSubject.next(false);
};

export const saveCorrection = async (update: Correction, forceRefresh = false) => {
  const { data, error } = await updateCorrection(update);

  if (data) {
    clearError();
    correctionSubject.next(data);
    fetchCorrections(currentFiltersSubject.getValue(), forceRefresh);
    fetchAttributions(kebabCase(update.combinedAssetType), update.combinedAssetId, true);
  }
  if (error) {
    addError('correction', error.message);
  }
  return { data, error };
};

export const removeCorrection = async (correction: Correction): Promise<void | number> => {
  const { error } = await deleteCorrection(correction.id);

  if (!error) {
    clearError();
    fetchCorrections(currentFiltersSubject.getValue(), true);
    fetchAttributions(kebabCase(correction.combinedAssetType), correction.combinedAssetId, true);
  } else {
    addError('correction', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }
};
